class CartUpsellCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dots = this.querySelector('[data-carousel-dots]');

    if (!this.track) return;

    this.onScroll = this.throttle(() => this.sync());
    this.onPrev = () => this.scrollByCards(-1);
    this.onNext = () => this.scrollByCards(1);
    this.onDotClick = (event) => {
      const dot = event.target.closest('[data-carousel-dot]');
      if (dot) this.scrollToCard(Number(dot.dataset.carouselDot));
    };

    // A cart update morphs this element in place, so connectedCallback does not run again
    // and the morph resets class/attributes to the server markup. Re-sync afterwards.
    this.onCartUpdate = () => requestAnimationFrame(() => this.sync());

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('cart:update', this.onCartUpdate);
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.dots?.addEventListener('click', this.onDotClick);
    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.track);
    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.dots?.removeEventListener('click', this.onDotClick);
    document.removeEventListener('cart:update', this.onCartUpdate);
    this.resizeObserver?.disconnect();
  }

  throttle(callback) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        callback();
      });
    };
  }

  get cards() {
    return Array.from(this.track?.children ?? []);
  }

  get step() {
    const cards = this.cards;
    if (cards.length < 2) return cards[0]?.offsetWidth ?? 0;
    return cards[1].offsetLeft - cards[0].offsetLeft;
  }

  scrollByCards(direction) {
    const step = this.step;
    if (!step) return;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  scrollToCard(index) {
    const step = this.step;
    if (!step) return;
    this.track.scrollTo({ left: step * index, behavior: 'smooth' });
  }

  syncDots(cardCount) {
    if (!this.dots) return;

    // The dots are rendered in Liquid so they survive a cart re-render, but rebuild
    // them if a re-render ever leaves the count out of step with the cards.
    if (this.dots.children.length !== cardCount) {
      this.dots.replaceChildren();
      if (cardCount > 1) {
        for (let i = 0; i < cardCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cart-upsell__dot';
          dot.dataset.carouselDot = String(i);
          dot.setAttribute('aria-label', `Go to suggestion ${i + 1}`);
          this.dots.appendChild(dot);
        }
      }
    }

    const step = this.step;
    const active = step ? Math.min(cardCount - 1, Math.round(this.track.scrollLeft / step)) : 0;
    Array.from(this.dots.children).forEach((dot, index) => {
      dot.classList.toggle('cart-upsell__dot--active', index === active);
    });
  }

  sync() {
    if (!this.track) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    this.prev?.toggleAttribute('disabled', this.track.scrollLeft <= 1);
    this.next?.toggleAttribute('disabled', this.track.scrollLeft >= maxScroll - 1);

    this.classList.toggle('cart-upsell--static', maxScroll <= 1);

    this.syncDots(this.cards.length);
  }
}

if (!customElements.get('cart-upsell-carousel')) {
  customElements.define('cart-upsell-carousel', CartUpsellCarousel);
}
